import * as React from 'react';
import {
  GridColumnResizeEvent,
  GridExpandChangeEvent,
  GridHeaderCellProps
} from '@progress/kendo-react-grid';
import { Component } from '../../dispatcher/modules';
import NavigationBaseGrid from './NavigationBaseGrid';
import List from './detail/list/List';
import { POS_LIST_ITEM } from './detail/list/Types';
import { Position } from '../../dispatcher/Types';
import _ from 'lodash';
import uuid from 'uuid-random';
import PuzzleButton from '../UI/button/PuzzleButton';
import {
  EXPAND_DETAIL_BUTTON_SYNTHETIC_EVENT,
  NAVIGATION_BASE_TREE_GRID_PROPS,
  NAVIGATION_BASE_TREE_GRID_STATE
} from './Types';
import WithTooltip from '../../utility/withTooltip';
import PuzzleTooltip from '../UI/tooltip/PuzzleTooltip';
import { SortDescriptor } from '@progress/kendo-data-query';

class NavigationBaseTreeGrid extends Component<NAVIGATION_BASE_TREE_GRID_PROPS, NAVIGATION_BASE_TREE_GRID_STATE> {
  tooltip: any = null;

  state:NAVIGATION_BASE_TREE_GRID_STATE = {
    data: [],
    expanded: false
  };

  private expandField = 'expanded';
  private expandedRowIds: { [key: string]: boolean } = {};
  private gridRef: NavigationBaseGrid;
  private lastExpandedOrCollapsedRowIndex: number;
  private listRefs = new Map();
  /**
   * If true table will be expanded user didn't click on toggle button
   * @private {Boolean} forceExpanded
   */
  private forceExpanded: boolean = false;

  constructor(props) {
    super(props);
    if (!this.moduleId) {
      this.moduleId = uuid();
    }
  }

  shouldComponentUpdate(nextProps: Readonly<any>, nextState: Readonly<any>, nextContext: any): boolean {
    return !(_.isEqual(this.props, nextProps) && _.isEqual(this.state, nextState));
  }

  private cellRender = (defaultRendering, cellProps) => {
    if (cellProps?.field === this.expandField) {
      const { children, ...tdProps } = defaultRendering.props;
      const { onClick } = children.props;
      const { name } = children.props.children.props;
      const detailExpandedButton = this.props.detailExpandedButton;

      if (detailExpandedButton) {
        defaultRendering = (
          <td {...tdProps}>
            {cellProps.dataItem?.[detailExpandedButton] > 0 && (
              <PuzzleButton
                fillMode="flat"
                iconClass={`k-icon k-i-${name}`}
                onClick={onClick}
                size="small"
              />
            )}
          </td>
        );
      } else {
        // If detailExpandedButton is not provided, just render the button without the condition
        defaultRendering = (
          <td {...tdProps}>
            <PuzzleButton
              fillMode="flat"
              iconClass={`k-icon k-i-${name}`}
              onClick={onClick}
              size="small"
            />
          </td>
        );
      }
    } else if (!React.isValidElement(defaultRendering)) {
      defaultRendering = {};
    }
    return defaultRendering;
  };

  private headerCellRender = (defaultRendering: React.ReactNode, props: GridHeaderCellProps) => (
    (props.field === this.expandField) && this.props.columns.some(column => !column.hidden)
      ? this.renderExpandAllToggle()
      : defaultRendering
  );

  private loadExpandedData = _.debounce(() => {
    if (this.gridRef && (this.state.expanded || this.forceExpanded)) {
      const { requestTreeListConfig, uniqueField } = this.props;
      let ind = 0;
      // @ts-ignore
      const records = this.gridRef.getVisibleRecords().filter((record) => {
        const hasSkeleton = record.subItems && record.subItems.every(_.isEmpty);
        // record.tms = [`TES_${++ind}`, `TES_${++ind}`];
        return record[uniqueField] && hasSkeleton;
      });
      if (records.length && requestTreeListConfig) {
        const { headers, method, params = {}, treeListRequestParamsMap, url } = requestTreeListConfig;
        let { rowId, rowIdMap, rowRepeatId, subRowIdMap, subRowId } = treeListRequestParamsMap;
        if (!rowIdMap) {
          rowIdMap = rowId;
        }
        let subRowIdMapValue = records.reduce((arr, record) => {
          if (!_.isEmpty(record[subRowId])) {
            arr.push(record[subRowId]);
          }
          return arr;
        }, []).join();
        if (!subRowIdMapValue) {
          subRowIdMapValue = undefined;
        }
        this.dispatch('navigationTreeGridDataListMulti', Position.end, {
          url,
          headers,
          treeListRequestParams: {
            [rowIdMap]: records.map(record => record[rowId]).join(),
            [subRowIdMap]: subRowIdMapValue,
            ...params
          },
          treeListOtherParams: {
            ...records.reduce((acc, record) => {
              acc[record[rowId]] = record[rowRepeatId];
              return acc;
            }, {})
          },
          method,
          treeDataItems: records,
          navigationTreeGridModuleId: this.props.moduleId,
          subRowIdMapValue,
          shouldUseSearchBar: this.props.shouldUseSearchBar
        });
      }
    }
  }, 250)

  parseGridData = (data: any) => {
    if (_.isFunction(this.props.parseGridData)) {
      data = this.props.parseGridData(data);
    }
    return data;
  };

  parseGridExtendedData = (data: any, treeListOtherParams: any) => {
    if (_.isFunction(this.props.parseGridExtendedData)) {
      data = this.props.parseGridExtendedData(data, treeListOtherParams);
    }
    return data;
  };

  /**
   * Prepare params for grid action request
   */
  prepareRequestParams = (pageNumber: number) => {
    const { pageSize, pageSizeName = 'pageSize', pageNumberName = 'pageNumber' } = this.props;
    let params = {};
    params[pageSizeName] = pageSize;
    params[pageNumberName] = pageNumber;
    return params;
  };

  /**
   * Sets NavigationBaseGrid reference
   * @param {NavigationBaseGrid} el
   */
  private setGridReference = (el: any) => {
    this.gridRef = el;
  };

  /**
   * Dispatcher load grid method
   * @param {Object} data
   * @param forceSkip
   */
  public setGridData = (data: any, forceSkip?: boolean) => {
    this.props.setGridData(data, forceSkip);
  };

  /**
   * @inheritDoc
   */
  public getFilterData = (isQuery = false) => this.gridRef.getFilterData(isQuery)

  /**
   * Return params needed for full dashboard grid request, including columns and advanced filter params
   * @param page
   */
  public getGridFilterParams = (page = 0) => this.gridRef.prepareRequestParams(page);

  public setFilterData = (data: any) => {
    this.gridRef.setFilterData(data);
  };

  public toggleExpanded = () => {
    this.setForceExpanded(this.forceExpanded && !this.state.expanded);
    this.setState(({ expanded }) => ({
      expanded: !expanded
    }), this.onExpandAllChange);
  };

  assignParams = (params: any, assignedParams: boolean = false) => {
    if (!assignedParams) {
      _.set(this.gridRef.requestConfig, 'params', params);
    } else {
      _.assign(this.gridRef.requestConfig?.params, params);
    }
  }

  requestData = (pageNumber: number = 0, skip: number = 0) => {
    this.gridRef.requestData(pageNumber, skip);
  }
  /**
   * Sync request data from hoc component
   * @param pageNumber
   * @param skip
   */
  syncRequestData = async (pageNumber: number = 0, skip: number = 0) => {
    await this.gridRef.syncRequestData(pageNumber, skip);
  }

  /**
   * Reset grid data and all paging params
   */
  resetGridData = () => {
    this.expandedRowIds = {};
    this.gridRef.resetGridData();
  };

  /**
   * Sync reset grid data and all paging params
   */
  syncResetGridData = async () => {
    this.expandedRowIds = {};
    await this.gridRef.syncResetGridData();
  };

  private expandChange = (event: GridExpandChangeEvent | EXPAND_DETAIL_BUTTON_SYNTHETIC_EVENT) => {
    const { value, dataItem, dataIndex } = event;
    const { requestTreeListConfig, uniqueField, onRowDeselected, isPreloadedSubitems = false } = this.props;
    const expandUniqueField = dataItem[uniqueField];

    this.gridRef.enableNavigation = !value;
    this.gridRef.getLastSelectedIndexByDataItem(dataItem);
    this.lastExpandedOrCollapsedRowIndex = dataIndex;

    if (!isPreloadedSubitems) {
      if (requestTreeListConfig && value) {
        const { url, method, params = {}, treeListRequestParamsMap, urlParams, headers } = requestTreeListConfig;
        let { rowId, rowIdMap, rowRepeatId, subRowIdMap, subRowId } = treeListRequestParamsMap;
        if (!_.isUndefined(urlParams)) {
          urlParams[rowId] = dataItem[rowId];
        }
        if (!rowIdMap) {
          rowIdMap = rowId;
        }
        let subRowIdMapValue;
        if (!_.isEmpty(dataItem[subRowId])) {
          subRowIdMapValue = dataItem[subRowId].join();
        }
        if (!subRowIdMapValue) {
          subRowIdMapValue = undefined;
        }
        this.dispatch('navigationTreeGridDataList', Position.end, {
          url,
          urlParams,
          headers,
          treeListRequestParams: {
            [rowIdMap]: dataItem[rowId],
            [subRowIdMap]: subRowIdMapValue,
            ...params
          },
          treeListOtherParams: {
            [rowRepeatId]: dataItem[rowRepeatId]
          },
          method,
          treeDataItem: dataItem,
          navigationTreeGridModuleId: this.props.moduleId,
          subRowIdMapValue,
          shouldUseSearchBar: this.props.shouldUseSearchBar
        });
      } else {
        this.setTreeGridDataList(dataItem);
        onRowDeselected?.();
      }
    } else {
      this.gridRef.setState({
        data: this.gridRef.state.data.map((item: any) => {
          if (item[uniqueField] === expandUniqueField) {
            item = {
              ...item,
              expanded: value
            };
          }
          return item;
        })
      }, () => {
        if (value) {
          this.setFocusToTreeList(dataItem);
          onRowDeselected?.();
        }
      });
    }
    this.expandedRowIds[expandUniqueField] = value;
  };

  /**
   * It happens on expand detail button and prepares params for `expandChange` method
   * @param {any} dataItem
   */
  expandDetailButtons = (dataItem) => {
    const syntheticEvent: EXPAND_DETAIL_BUTTON_SYNTHETIC_EVENT = {
      value: dataItem.expanded,
      dataItem,
      dataIndex: dataItem.Index
    };
    this.expandChange(syntheticEvent);
  }

  /**
   * Expands or collapses overflowing text
   * @param {any} dataItem
   */
  expandTextButtons = (dataItem) => {
    const { uniqueField } = this.props;
    const expandUniqueField = dataItem[uniqueField];
    this.gridRef.setState({
      data: this.gridRef.state.data.map((item: any) => {
        if (item[uniqueField] === expandUniqueField) {
          item = {
            ...item,
            expandedText: dataItem.expandedText
          };
        }
        return item;
      })
    });
  }

  /**
   * Sets forceExpanded property to be true or false
   * @param {Boolean} forceExpanded
   */
  setForceExpanded = (forceExpanded?: boolean) => {
    this.forceExpanded = forceExpanded;
  }

  /**
   * Set sort in NavigationBaseGrid
   * @param {SortDescriptor[]} sort
   */
  public setSort = (sort: SortDescriptor[]):void => {
    this.gridRef.setSort(sort);
  }

  onExpandAllChange = (refreshed?: boolean) => {
    let { expanded } = this.state;
    if (this.forceExpanded) {
      expanded = true;
    }
    if (!expanded) {
      this.gridRef.isCollapsedAll = true;
    } else {
      this.gridRef.isExpandedAll = true;
    }
    const { uniqueField, detailExpandedButton, isPreloadedSubitems } = this.props;
    const { data } = this.gridRef.state;

    this.gridRef.setState({
      data: data.map((dataItem, index) => {
        const { expanded: alreadyExpanded } = dataItem;
        const uniqueValue = dataItem[uniqueField];
        const numberOfSubItems = dataItem[detailExpandedButton];

        if (uniqueValue) {
          this.expandedRowIds[uniqueValue] = expanded;
        }

        if (refreshed) {
          if (expanded) {
            return {
              ...dataItem,
              expanded: detailExpandedButton ? (numberOfSubItems !== 0 && numberOfSubItems !== undefined) ? true : alreadyExpanded : true,
              subItems: _.isEmpty(dataItem.subItems) ? [{}] : dataItem.subItems
            };
          } else {
            return dataItem;
          }
        } else {
          return (alreadyExpanded && expanded) ? dataItem : {
            ...dataItem,
            expanded: detailExpandedButton ? (numberOfSubItems !== 0 && numberOfSubItems !== undefined) ? expanded : alreadyExpanded : expanded,
            subItems: isPreloadedSubitems || !_.isEmpty(dataItem.subItems) ? dataItem.subItems : expanded ? [{}] : undefined
          };
        }
      })
    });
  };

  setTreeGridDataList = (treeDataItem, treeGridDataList?) => {
    const { requestTreeListConfig, uniqueField } = this.props;
    const { rowId } = requestTreeListConfig.treeListRequestParamsMap;
    const expandUniqueField = treeDataItem[uniqueField];
    let isRowExpanded = treeDataItem.expanded;
    const treeGridRef = this;
    this.gridRef.setState({
      data: this.gridRef.state.data.map((item: any) => {
        if (item[uniqueField] === expandUniqueField) {
          item = {
            ...item,
            expanded: !isRowExpanded
          };
          if (treeGridDataList) {
            item.subItems = treeGridDataList.content[treeDataItem[rowId]];
            this.expandedRowIds[expandUniqueField] = true;
          }
        }
        return item;
      })
    }, () => {
      if (!isRowExpanded) {
        treeGridRef.setFocusToTreeList(treeDataItem);
      }
    });
  };

  /**
   * Sets focus to list item with position 'pos'
   * @param {any} treeDataItem
   * @param {POS_LIST_ITEM} pos
   */
  setFocusToTreeList = (treeDataItem: any, pos?: POS_LIST_ITEM) => {
    const { uniqueField } = this.props;
    const expandUniqueField = treeDataItem[uniqueField];
    this.listRefs.get(expandUniqueField).setFocusToTreeListItem(pos);
  }

  /**
   * Checks if some list (that has 'treeDataItem' record) item has focus
   * @param treeDataItem
   */
  isListItemFocused = (treeDataItem: any) => {
    const { uniqueField } = this.props;
    const expandUniqueField = treeDataItem && treeDataItem[uniqueField];
    return this.listRefs.get(expandUniqueField)?.isListItemFocused();
  }

  /**
   * Returns true if uniqueField from last record from NavigationBaseGrid is the same as id
   * @param {string} id
   */
  isLastRecord = (id: string): boolean => this.gridRef.isLastRecord(id);

  /**
   * Set lastSelectedItemId in List
   * Trigger if user focus last element of last child with shift-tab (form the record below)
   * @param {string} dataItemId
   * @param {string} id
   */
  setLastSelectedListItemId = (dataItemId: string, id: string): void => {
    this.listRefs.get(dataItemId)?.setLastSelectedListItemId(id);
  }

  /**
   * Setter for lastSelectedIndex in NavigationBaseGrid
   * @param {any} dataItem
   * @param {any} subDataItem
   */
  setLastSelectedIndex = (dataItem: any, subDataItem: any): void => {
    this.gridRef.setLastSelectedIndex({
      dataItem,
      subDataItem
    });
  }

  setEnableNavigation = (enableNavigation: boolean): void => {
    this.gridRef.setEnableNavigation(enableNavigation);
  }

  /**
   * Scroll grid to left side
   */
  scrollToLeft = (): void => {
    this.gridRef.scrollToLeft();
  }

  /**
   * Returns true if last index is the same as data length in NavigationBaseGrid
   */
  isLastIndexOfLastRecord = (): boolean => this.gridRef.isLastIndexOfLastRecord();

  setSubItems = (listsData) => {
    const { requestTreeListConfig, uniqueField } = this.props;
    const { rowId } = requestTreeListConfig.treeListRequestParamsMap;
    const { content } = listsData;
    let prop;

    this.gridRef.setState(prevState => ({
      data: prevState.data.map((dataItem) => {
        prop = dataItem[rowId];
        if (dataItem.expanded && content.hasOwnProperty(prop)) {
          const subItems = content[prop];
          if (subItems) {
            this.expandedRowIds[dataItem[uniqueField]] = true;
            dataItem = {
              ...dataItem,
              subItems
            };
          }
        } else if (!(dataItem?.subItems?.length && !_.isEmpty(dataItem.subItems[0]))) {
          this.expandedRowIds[dataItem[uniqueField]] = false;
          dataItem = {
            ...dataItem,
            expanded: false,
            subItems: undefined
          };
        }
        return dataItem;
      })
    }));
  };

  private updateTreeGridDataListItem = (treeDataItem) => {
    const { uniqueField } = this.props;
    const expandUniqueField = treeDataItem[uniqueField];
    let itemFound = false;
    this.gridRef.setState({
      data: this.gridRef.state.data.map((item: any) => {
        if (!itemFound && item.expanded) {
          let subItems = item.subItems.map((subItem) => {
            if (subItem[uniqueField] === expandUniqueField) {
              itemFound = true;
              subItem = {
                ...treeDataItem
              };
            }
            return subItem;
          });
          if (itemFound) {
            item = {
              ...item,
              subItems
            };
          }
        }
        return item;
      })
    });
  };

  onChangeItem = (dataItem) => {
    this.updateTreeGridDataListItem(dataItem);
  };

  private onColumnResize = ({ columns, end, index, newWidth }: GridColumnResizeEvent) => {
    const changedColumn = columns[index - 1];
    if (changedColumn) {
      const { field } = changedColumn;
      if (newWidth === 0) {
        newWidth = changedColumn['width'] as number;
      }

      this.listRefs.forEach((list) => {
        list.rowRefs.forEach((row) => {
          const cell = row.cellNodes.get(field);
          if (cell) {
            cell.style.minWidth = `${newWidth}px`;
          }
        });
      });

      if (end) {
        this.dispatch('ReconfigureColumns', Position.end, {
          columns: this.props.columns.map((column) => {
            if (column.dataIndex === field) {
              column = {
                ...column,
                width: newWidth
              };
            }
            return column;
          })
        });
      }
    }
  };

  renderDetail = (props: any) => {
    const {
      contextMenuDetail,
      rowHeight,
      uniqueField,
      uniqueFieldDetail,
      onDetailItemDoubleClick,
      onRowSelected,
      displayDetailListRows,
      renderedDetailListRows
    } = this.props;
    return (
      <List
        {...props}
        ref={(list) => {
          const refId = props.dataItem[uniqueField];
          if (list) {
            this.listRefs.set(refId, list);
          } else {
            this.listRefs.delete(refId);
          }
        }}
        columns={this.props.columns}
        contextMenu={contextMenuDetail}
        rowHeight={rowHeight}
        onChangeItem={this.onChangeItem}
        onDetailItemDoubleClick={onDetailItemDoubleClick}
        uniqueField={uniqueFieldDetail}
        onKeyDownListItem={(e, enableNavigation, dataItem?, isSetNextRow?) => {
          this.gridRef.setRowSelect(e, enableNavigation, dataItem, isSetNextRow);
        }}
        scrollToLeft={this.scrollToLeft}
        setLastSelectedIndex={this.setLastSelectedIndex}
        setEnableNavigation={this.setEnableNavigation}
        isLastIndexOfLastRecord={this.isLastIndexOfLastRecord}
        isLastRecord={this.isLastRecord}
        onSetSelectedRowDataItem={(subItem, isSubItem = false) => {
          this.gridRef.setSelectedRowDataItem(subItem);
          onRowSelected?.(subItem, isSubItem);
        }}
        displayDetailListRows={displayDetailListRows}
        renderedDetailListRows={renderedDetailListRows}
      />
    );
  };

  renderExpandAllToggle = () => {
    const { expanded } = this.state;
    const title = expanded ? this.translate('shrinkAll') : this.translate('expandAll');
    return (
      <>
        <WithTooltip
          key={expanded ? 'notExpanded' : 'expanded'}
          context={this}
          title={title}
          htmlTag={'span'}
        >
          <PuzzleButton
            data-cy={'expandShrinkBtn'}
            fillMode="flat"
            titleText={title}
            themeColor="primary"
            iconClass={`tp-grid-expand-toggle tp-pointable tp-icon tp-${this.state.expanded ? 'collapse' : 'expand'}-list`}
            onClick={this.toggleExpanded}
            size="small"
          />
        </WithTooltip>
        <PuzzleTooltip
          anchorElement="target"
          openDelay={500}
          position="bottom"
          ref={(el: any) => this.tooltip = el}
        />
      </>
    );
  }

  /**
   * Set new data with the changed list items
   * @param {string} parentId
   * @param {any} changedData
   */
  public changeListItems = (parentId: string, changedData: any): void => {
    let { data } = this.gridRef.state;
    const { uniqueField } = this.props;

    const newData: any[] = data.map((item) => {
      if (parentId === item[uniqueField]) {
        if (_.isFunction(this.props.parseGridExtendedData)) {
          const { requestTreeListConfig } = this.props;
          const subItem = {};
          subItem[parentId] = changedData;
          const { treeListRequestParamsMap } = requestTreeListConfig;
          let { rowId, rowIdMap, rowRepeatId } = treeListRequestParamsMap;
          if (!rowIdMap) {
            rowIdMap = rowId;
          }

          const propertyName = item[rowId];
          const treeListOtherParams = {};
          treeListOtherParams[propertyName] = item[rowRepeatId];

          const { content } = this.parseGridExtendedData(subItem, treeListOtherParams);
          const parsedSubItems = content[parentId];
          return Object.assign(item, {
            subItems: parsedSubItems
          });
        } else {
          return Object.assign(item, { subItems: changedData });
        }
      } else {
        return item;
      }
    });

    this.gridRef.setState({ data: newData });
  }

  /**
   * Moves vertical scroll on grid to desired position
   * @param {number} scrollPosition
   */
  public scrollToLastPosition = (scrollPosition: number): void => {
    this.gridRef.scrollToLastPosition(scrollPosition);
  };

  /**
   * Check if totalElements from get a response is different from total in state
   * @param {number} totalElements
   */
  public setIsTotalChanged = (totalElements:number):void => {
    this.gridRef.setIsTotalChanged(totalElements);
  }

  /**
   * Reload All Grid Data and Scroll to top
   */
  public reloadGridDataAndScrollTop = (params: object = {}, url = '', method: '') => {
    this.gridRef.reloadGridData(params, url, method, false);
  }

  /**
   * Sync Reload All Grid Data
   * @param params
   * @param url
   * @param method
   * @param saveScroll
   */
  syncReloadGridData = async (params: object = {}, url = '', method: '', saveScroll: boolean = true) => await this.gridRef.syncReloadGridData(params, url, method, saveScroll);

  renderBody() {
    const { className, style, moduleId = this.moduleId, isTree = true } = this.props;
    return (
      <NavigationBaseGrid
        {...this.props}
        moduleId={moduleId}
        cellRender={this.cellRender}
        className={`tp-navigation-base-tree-grid ${className}`}
        detail={this.renderDetail}
        expandField={this.expandField}
        headerCellRender={this.headerCellRender}
        loadExpandedData={this.loadExpandedData}
        onColumnResize={this.onColumnResize}
        onExpandChange={this.expandChange}
        ref={this.setGridReference}
        rowSkeletonEnabled
        style={style}
        setFocusToTreeList={this.setFocusToTreeList}
        isListItemFocused={this.isListItemFocused}
        isTree={isTree}
        setForceExpanded={this.setForceExpanded}
        setLastSelectedListItemId={this.setLastSelectedListItemId}
        onExpandDetailButtonChange={this.expandDetailButtons}
        onExpandTextButtonChange={this.expandTextButtons}
      />
    );
  }
}

export default NavigationBaseTreeGrid;
